import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Check, Shield, Smartphone, Droplets, ShoppingCart, Minus, Plus, Copy, CreditCard } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShopPage() {
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderResult, setOrderResult] = useState(null);

  const products = [
    {
      id: 'nfc-tag-standard',
      name: 'SaferPaws NFC Tag',
      description: 'Waterproof NFC smart tag with QR code. Works with any smartphone.',
      price: 19.99,
      image: 'https://customer-assets.emergentagent.com/job_nfc-pet-tags/artifacts/yv3s6609_Screenshot_20260529_155704_Alibabacom.png',
      features: ['Waterproof', 'No app needed', 'QR code backup', 'NFC + QR']
    }
  ];

  const [checkoutForm, setCheckoutForm] = useState({
    customer_name: '',
    customer_email: '',
    shipping_address: '',
    payment_method: 'stripe'
  });

  const addToCart = (product) => {
    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      setCart(cart.map(item =>
        item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    toast.success(`${product.name} added to cart`);
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.id === productId) {
        const newQuantity = item.quantity + delta;
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        items: cart.map(item => ({
          product_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        customer_name: checkoutForm.customer_name,
        customer_email: checkoutForm.customer_email,
        shipping_address: checkoutForm.shipping_address,
        payment_method: checkoutForm.payment_method
      };
      const response = await axios.post(`${BACKEND_URL}/api/orders`, payload);
      setOrderResult(response.data);
      setOrderComplete(true);
      setCart([]);
      toast.success('Order placed successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  if (orderComplete && orderResult) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-20">
          <div className="max-w-2xl mx-auto px-4">
            <div className="bg-card rounded-2xl p-8 sm:p-10 border border-border shadow-sm">
              <div className="text-center">
                <div className="w-20 h-20 bg-primary/15 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-10 h-10 text-primary" />
                </div>
                <h1 className="text-3xl font-heading font-bold text-foreground" data-testid="order-confirmed-heading">
                  Order Confirmed!
                </h1>
                <p className="mt-2 text-muted-foreground">
                  Order <strong className="text-foreground">{orderResult.order_id}</strong> — Total <strong className="text-foreground">£{orderResult.total.toFixed(2)}</strong>
                </p>
                <p className="mt-3 text-sm text-muted-foreground bg-card/60 rounded-lg p-3 border border-border">
                  A confirmation email with your tag activation codes has been sent to your inbox.
                </p>
              </div>

              {/* Tag Codes Display */}
              <div className="mt-8">
                <h2 className="text-lg font-heading font-semibold text-foreground mb-3">
                  Your Tag Activation Code{orderResult.tag_codes.length > 1 ? 's' : ''} ({orderResult.tag_codes.length})
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Each code below is linked to one physical tag. Once you receive your tag(s), use the matching code at the activation page to set up your pet&apos;s profile.
                </p>
                <div className="space-y-2">
                  {orderResult.tag_codes.map((code, idx) => (
                    <div
                      key={code}
                      className="flex items-center justify-between bg-secondary/40 border border-border rounded-lg px-4 py-3"
                      data-testid={`tag-code-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground font-medium w-5">#{idx + 1}</span>
                        <code className="font-mono text-lg font-bold text-primary tracking-widest">
                          {code}
                        </code>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copyCode(code)}
                          className="text-muted-foreground hover:text-foreground p-2 rounded-md hover:bg-muted transition-colors"
                          title="Copy code"
                          data-testid={`copy-code-${idx}`}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <Link to={`/activate/${code}`}>
                          <Button size="sm" variant="outline" data-testid={`activate-code-${idx}`}>
                            Activate
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link to="/activate" className="w-full">
                  <Button
                    className="w-full bg-primary hover:bg-primary/90 rounded-full"
                    data-testid="activate-after-order-btn"
                  >
                    Activate a Tag
                  </Button>
                </Link>
                <Link to="/" className="w-full">
                  <Button variant="outline" className="w-full rounded-full border-primary/40 text-primary hover:bg-primary/10" data-testid="continue-shopping-btn">
                    Continue Shopping
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-24 pb-20" data-testid="shop-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-foreground tracking-tight">
              Shop SaferPaws Tags
            </h1>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose the perfect tag for your furry friend. All tags include free activation and profile access.
            </p>
          </div>

          {/* Features Bar */}
          <div className="flex flex-wrap justify-center gap-6 mb-16">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Shield className="w-5 h-5 text-primary" />
              <span>UK Based</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Smartphone className="w-5 h-5 text-primary" />
              <span>Works with Any Phone</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Droplets className="w-5 h-5 text-primary" />
              <span>100% Waterproof</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Products Grid */}
            <div className="lg:col-span-2 space-y-6">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="bg-card rounded-xl border border-border p-6 relative"
                  data-testid={`product-${product.id}`}
                >
                  <div className="flex flex-col sm:flex-row gap-6">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full sm:w-40 h-40 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h3 className="text-xl font-heading font-semibold text-foreground">
                        {product.name}
                      </h3>
                      <p className="mt-2 text-muted-foreground text-sm">
                        {product.description}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {product.features.map((feature, idx) => (
                          <span
                            key={idx}
                            className="text-xs bg-secondary text-secondary-foreground px-2 py-1 rounded-full"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-2xl font-bold text-foreground">
                          £{product.price.toFixed(2)}
                        </span>
                        <Button
                          onClick={() => addToCart(product)}
                          className="bg-primary hover:bg-primary/90 rounded-full px-6"
                          data-testid={`add-to-cart-${product.id}`}
                        >
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Add to Cart
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Cart / Checkout */}
            <div className="lg:col-span-1">
              <div className="bg-card rounded-xl border border-border p-6 sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-heading font-semibold text-foreground">
                    {showCheckout ? 'Checkout' : 'Your Cart'}
                  </h2>
                  {!showCheckout && cartItemCount > 0 && (
                    <span className="text-xs bg-primary text-primary-foreground rounded-full px-2 py-0.5 font-medium" data-testid="cart-count">
                      {cartItemCount}
                    </span>
                  )}
                </div>

                {!showCheckout ? (
                  <>
                    {cart.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">
                        Your cart is empty
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {cart.map((item) => (
                          <div key={item.id} className="flex items-center gap-3 py-3 border-b border-border">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-12 h-12 rounded object-cover"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {item.name}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                £{item.price.toFixed(2)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => updateQuantity(item.id, -1)}
                                className="w-6 h-6 rounded bg-secondary flex items-center justify-center hover:bg-secondary/80"
                                data-testid={`decrease-${item.id}`}
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="w-6 text-center text-sm">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.id, 1)}
                                className="w-6 h-6 rounded bg-secondary flex items-center justify-center hover:bg-secondary/80"
                                data-testid={`increase-${item.id}`}
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                        <div className="pt-4 border-t border-border">
                          <div className="flex justify-between text-lg font-semibold">
                            <span>Total</span>
                            <span data-testid="cart-total">£{cartTotal.toFixed(2)}</span>
                          </div>
                        </div>
                        <Button
                          onClick={() => setShowCheckout(true)}
                          className="w-full bg-primary hover:bg-primary/90 rounded-full py-6"
                          data-testid="proceed-checkout-btn"
                        >
                          Proceed to Checkout
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <form onSubmit={handleCheckout} className="space-y-4">
                    <div>
                      <Label htmlFor="customer_name">Full Name</Label>
                      <Input
                        id="customer_name"
                        value={checkoutForm.customer_name}
                        onChange={(e) => setCheckoutForm({ ...checkoutForm, customer_name: e.target.value })}
                        required
                        data-testid="checkout-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="customer_email">Email</Label>
                      <Input
                        id="customer_email"
                        type="email"
                        value={checkoutForm.customer_email}
                        onChange={(e) => setCheckoutForm({ ...checkoutForm, customer_email: e.target.value })}
                        required
                        data-testid="checkout-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping_address">Shipping Address</Label>
                      <Input
                        id="shipping_address"
                        value={checkoutForm.shipping_address}
                        onChange={(e) => setCheckoutForm({ ...checkoutForm, shipping_address: e.target.value })}
                        placeholder="Including UK postcode"
                        required
                        data-testid="checkout-address-input"
                      />
                    </div>
                    <div>
                      <Label>Payment Method</Label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <button
                          type="button"
                          onClick={() => setCheckoutForm({ ...checkoutForm, payment_method: 'stripe' })}
                          className={`p-3 rounded-lg border text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                            checkoutForm.payment_method === 'stripe'
                              ? 'border-[#635bff] bg-[#635bff]/5 text-[#635bff]'
                              : 'border-border text-muted-foreground hover:border-[#635bff]/50'
                          }`}
                          data-testid="payment-stripe"
                        >
                          <CreditCard className="w-4 h-4" />
                          Stripe
                        </button>
                        <button
                          type="button"
                          onClick={() => setCheckoutForm({ ...checkoutForm, payment_method: 'paypal' })}
                          className={`p-3 rounded-lg border text-sm font-semibold transition-colors flex items-center justify-center gap-1 ${
                            checkoutForm.payment_method === 'paypal'
                              ? 'border-[#003087] bg-[#003087]/5 text-[#003087]'
                              : 'border-border text-muted-foreground hover:border-[#003087]/50'
                          }`}
                          data-testid="payment-paypal"
                        >
                          <span className="font-bold italic"><span className="text-[#003087]">Pay</span><span className="text-[#0070ba]">Pal</span></span>
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground bg-card/60 rounded-lg p-2 border border-border">
                      Secure checkout. Your card details are handled by our payment provider.
                    </p>
                    <div className="pt-4 border-t border-border">
                      <div className="flex justify-between text-lg font-semibold mb-4">
                        <span>Total</span>
                        <span>£{cartTotal.toFixed(2)}</span>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-primary hover:bg-primary/90 rounded-full py-6"
                      disabled={loading}
                      data-testid="place-order-btn"
                    >
                      {loading ? 'Processing...' : `Pay £${cartTotal.toFixed(2)}`}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full"
                      onClick={() => setShowCheckout(false)}
                    >
                      Back to Cart
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
